<template>
  <div
    class="digital-sign-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="digital-sign-title"
  >
    <div
      class="digital-sign-drawer"
      @click.stop
    >
      <div class="drawer-indicator"></div>

      <header class="drawer-header">
        <div class="header-icon">
          <span>✓</span>
        </div>

        <div class="header-text">
          <h2 id="digital-sign-title">
            Электронное подтверждение
          </h2>

          <p>
            Перед созданием компании
          </p>
        </div>

        <button
          class="close-button"
          type="button"
          :disabled="isLoading"
          aria-label="Закрыть"
          @click="handleClose"
        >
          ×
        </button>
      </header>

      <div class="drawer-divider"></div>

      <div class="drawer-content">
        <p class="introductory-text">
          Перед созданием карточки компании внимательно ознакомьтесь
          с настоящими условиями.
          <br /><br />
          Продолжая, вы подтверждаете, что понимаете назначение данного
          раздела и добровольно принимаете указанные условия.
        </p>

        <section
          v-for="section in licenceSections"
          :key="section.title"
          class="licence-section"
        >
          <h3>{{ section.title }}</h3>

          <p
            v-if="section.text"
            class="section-text"
          >
            {{ section.text }}
          </p>

          <ul v-if="section.bullets?.length">
            <li
              v-for="item in section.bullets"
              :key="item"
            >
              {{ item }}
            </li>
          </ul>
        </section>

        <section class="confirmation-card">
          <div class="confirmation-title">
            <span class="confirmation-icon">
              ✓
            </span>

            <span>
              Электронное подтверждение
            </span>
          </div>

          <p class="confirmation-subtitle">
            Нажимая кнопку «Принять», вы подтверждаете, что:
          </p>

          <ul>
            <li>
              Полностью ознакомились с условиями.
            </li>

            <li>
              Добровольно принимаете настоящие условия.
            </li>

            <li>
              Понимаете значение создания карточки.
            </li>

            <li>
              Соглашаетесь с модерацией карточки администрацией Hr3.
            </li>

            <li>
              Соглашаетесь с сохранением электронного подтверждения.
            </li>
          </ul>

          <p class="confirmation-description">
            Hr3 сохраняет дату и время принятия условий, идентификатор
            вашей учётной записи, версию приложения и техническую
            информацию о совершённом действии.
          </p>
        </section>

        <div
          class="confirmation-checkbox"
          :class="{ checked: isConfirmed }"
          role="checkbox"
          :aria-checked="isConfirmed"
          tabindex="0"
          @click="toggleConfirmation"
          @keydown.space.prevent="toggleConfirmation"
          @keydown.enter.prevent="toggleConfirmation"
        >
          <span class="custom-checkbox">
            <span v-if="isConfirmed">
              ✓
            </span>
          </span>

          <span class="checkbox-text">
            Я ознакомился с условиями и принимаю их
          </span>
        </div>

        <transition name="error">
          <div
            v-if="errorMessage"
            class="error-message"
          >
            <span class="error-icon">
              !
            </span>

            <span>
              {{ errorMessage }}
            </span>
          </div>
        </transition>
      </div>

      <div class="drawer-divider"></div>

      <footer class="drawer-footer">
        <button
          class="secondary-button"
          type="button"
          :disabled="isLoading"
          @click="handleClose"
        >
          Не сейчас
        </button>

        <button
          class="accept-button"
          type="button"
          :disabled="!isConfirmed || isLoading"
          @click="createDigitalSign"
        >
          <span
            v-if="isLoading"
            class="button-loader"
          ></span>

          <span v-else>
            ✓
          </span>

          {{
            isLoading
              ? "Принимаем..."
              : "Принять"
          }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

import DigitalSignService
  from "../api/DigitalSignService.js";

// MARK: - Emits

const emit = defineEmits([
  "accepted",
  "close"
]);

// MARK: - State

const isConfirmed = ref(false);
const isLoading = ref(false);
const errorMessage = ref("");

// MARK: - Licence Content

const licenceSections = [
  {
    title: "Что означает создание карточки",

    text:
      "Карточка компании является информационной страницей в каталоге работодателей Hr3.\n\n" +
      "Её создание не означает, что вы являетесь владельцем компании, официальным представителем или лицом, уполномоченным действовать от её имени.\n\n" +
      "Создание карточки не предоставляет исключительных прав на название, бренд, товарные знаки или другую интеллектуальную собственность компании."
  },

  {
    title: "Ваши подтверждения",

    bullets: [
      "Публикуемая информация, насколько вам известно, является достоверной.",

      "Вы не размещаете заведомо ложную, незаконную или вводящую в заблуждение информацию.",

      "Вы понимаете, что несёте ответственность за сведения, опубликованные от вашего имени."
    ]
  },

  {
    title: "Управление карточкой",

    text:
      "После создания карточки вам может быть предоставлено право её редактирования исключительно для поддержания информации в актуальном состоянии.\n\n" +
      "Другие пользователи могут запросить право редактирования. Решение о предоставлении доступа принимает текущий редактор либо администрация Hr3.\n\n" +
      "Hr3 вправе изменить, ограничить или прекратить право редактирования карточки."
  },

  {
    title: "Модерация",

    text:
      "Hr3 вправе проверять опубликованные карточки и при необходимости:",

    bullets: [
      "Изменять информацию и исправлять ошибки.",

      "Объединять дублирующиеся карточки.",

      "Ограничивать или отзывать права редактирования.",

      "Скрывать или удалять карточки.",

      "Удалять информацию, нарушающую законодательство или права третьих лиц."
    ]
  },

  {
    title: "Пользователи и место работы",

    text:
      "Пользователи Hr3 могут самостоятельно указывать место своей работы.\n\n" +
      "Hr3 не осуществляет обязательную предварительную проверку факта трудоустройства. При получении обращения от компании сведения могут быть проверены, изменены или удалены."
  },

  {
    title: "Обращения компаний",

    text:
      "Компания или её представитель может обратиться в Hr3 с запросом:",

    bullets: [
      "Об исправлении информации.",

      "О передаче прав управления карточкой.",

      "Об удалении карточки.",

      "О рассмотрении других вопросов, связанных с опубликованной информацией."
    ]
  }
];

// MARK: - Actions

function handleClose() {
  if (isLoading.value) {
    return;
  }

  emit("close");
}

function toggleConfirmation() {
  if (isLoading.value) {
    return;
  }

  isConfirmed.value = !isConfirmed.value;
  errorMessage.value = "";
}

async function createDigitalSign() {
  if (
    !isConfirmed.value ||
    isLoading.value
  ) {
    return;
  }

  try {
    isLoading.value = true;
    errorMessage.value = "";

    console.log(
      "✍️ Создаём электронное подтверждение..."
    );

    const response =
      await DigitalSignService
        .setCompanyDigitalSign();

    console.log(
      "📦 Ответ DigitalSignService:",
      response
    );

    /*
      Не требуем обязательный id.

      Если запрос успешно завершился —
      считаем подтверждение принятым.
    */

    const signId =
      response?.id ||
      response?.digital_sign_id ||
      response?.digitalSignId ||
      response?.data?.id ||
      response?.data?.digital_sign_id ||
      null;

    console.log(
      "✅ Электронное подтверждение принято",
      {
        signId,
        response
      }
    );

    emit(
      "accepted",
      signId
    );

  } catch (error) {
    console.error(
      "❌ Ошибка электронной подписи:",
      {
        status:
          error?.response?.status,

        data:
          error?.response?.data,

        message:
          error?.message,

        error
      }
    );

    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Не удалось принять условия. Попробуйте ещё раз.";
  } finally {
    isLoading.value = false;
  }
}

</script>

<style scoped>
/* Overlay */

.digital-sign-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: clamp(
    16px,
    3vw,
    32px
  );

  overflow: hidden;
  box-sizing: border-box;

  background: rgba(10, 13, 20, 0.7);

  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);

  animation:
    overlay-appear
    0.25s
    ease;
}

.digital-sign-overlay *,
.digital-sign-overlay *::before,
.digital-sign-overlay *::after {
  box-sizing: border-box;
}

/* Drawer */

.digital-sign-drawer {
  width: 100%;
  height: 100%;

  max-width: 1180px;
  max-height: 920px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 26px;

  box-shadow:
    0 40px 110px
    rgba(0, 0, 0, 0.42);

  animation:
    drawer-appear
    0.4s
    cubic-bezier(0.22, 1, 0.36, 1);
}

.drawer-indicator {
  width: 42px;
  height: 5px;

  flex-shrink: 0;
  align-self: center;

  margin-top: 11px;

  background: #d5d5d8;
  border-radius: 999px;
}

/* Header */

.drawer-header {
  display: flex;
  align-items: center;
  gap: 16px;

  flex-shrink: 0;

  padding:
    17px
    40px
    22px;
}

.header-icon {
  width: 54px;
  height: 54px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #6758e8
    );

  border-radius: 16px;

  box-shadow:
    0 8px 20px
    rgba(65, 105, 230, 0.25);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 22px;
}

.header-text {
  min-width: 0;
  flex: 1;
}

.header-text h2 {
  margin: 0;

  color: #202124;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 21px;
  line-height: 1.3;
}

.header-text p {
  margin: 4px 0 0;

  color: #858992;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
}

.close-button {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #74777e;
  background: #f1f2f4;

  border: none;
  border-radius: 50%;

  font-size: 24px;
  font-weight: 300;
  line-height: 1;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.close-button:hover:not(:disabled) {
  color: #33363c;
  background: #e7e8eb;

  transform: rotate(5deg);
}

.close-button:disabled {
  cursor: default;
  opacity: 0.5;
}

/* Divider */

.drawer-divider {
  width: 100%;
  height: 1px;

  flex-shrink: 0;

  background: rgba(0, 0, 0, 0.07);
}

/* Content */

.drawer-content {
  min-width: 0;
  min-height: 0;
  flex: 1;

  overflow-x: hidden;
  overflow-y: auto;

  padding:
    36px
    48px;

  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.drawer-content::-webkit-scrollbar {
  width: 8px;
}

.drawer-content::-webkit-scrollbar-track {
  background: transparent;
}

.drawer-content::-webkit-scrollbar-thumb {
  background: rgba(110, 116, 128, 0.3);
  border: 2px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
}

.introductory-text,
.licence-section,
.confirmation-card,
.confirmation-checkbox,
.error-message {
  width: 100%;
  max-width: 940px;

  margin-left: auto;
  margin-right: auto;
}

/* Intro */

.introductory-text,
.section-text {
  color: #777b84;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;

  white-space: pre-line;
}

.introductory-text {
  margin-top: 0;
  margin-bottom: 0;

  padding:
    22px
    24px;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.07),
      rgba(103, 88, 232, 0.04)
    );

  border: 1px solid rgba(40, 135, 247, 0.13);
  border-radius: 18px;
}

/* Sections */

.licence-section {
  margin-top: 30px;
}

.licence-section h3 {
  margin: 0 0 12px;

  color: #25262a;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.35;
}

.section-text {
  margin: 0;
}

.licence-section ul,
.confirmation-card ul {
  display: flex;
  flex-direction: column;
  gap: 10px;

  margin: 14px 0 0;
  padding: 0;

  list-style: none;
}

.licence-section li,
.confirmation-card li {
  position: relative;

  padding-left: 20px;

  color: #777b84;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.55;
}

.licence-section li::before,
.confirmation-card li::before {
  content: "";

  position: absolute;
  top: 8px;
  left: 1px;

  width: 7px;
  height: 7px;

  background: #2887f7;
  border-radius: 50%;

  box-shadow:
    0 0 0 4px
    rgba(40, 135, 247, 0.09);
}

/* Confirmation card */

.confirmation-card {
  margin-top: 34px;

  padding:
    22px
    24px;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.08),
      rgba(103, 88, 232, 0.05)
    );

  border: 1px solid rgba(40, 135, 247, 0.16);
  border-radius: 20px;
}

.confirmation-title {
  display: flex;
  align-items: center;
  gap: 11px;

  color: #25262a;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
}

.confirmation-icon {
  width: 28px;
  height: 28px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #2887f7;
  border-radius: 50%;

  box-shadow:
    0 5px 12px
    rgba(40, 135, 247, 0.25);

  font-size: 13px;
}

.confirmation-subtitle {
  margin: 17px 0 0;

  color: #34363b;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
}

.confirmation-description {
  margin: 18px 0 0;

  color: #777b84;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.55;
}

/* Checkbox */

.confirmation-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 13px;

  margin-top: 22px;

  padding:
    17px
    18px;

  background: #f7f8fa;
  border: 1px solid #e3e6eb;
  border-radius: 17px;

  cursor: pointer;

  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.confirmation-checkbox:hover {
  border-color: rgba(40, 135, 247, 0.3);

  box-shadow:
    0 7px 20px
    rgba(28, 52, 90, 0.06);
}

.confirmation-checkbox.checked {
  background: rgba(40, 135, 247, 0.07);
  border-color: rgba(40, 135, 247, 0.4);
}

.confirmation-checkbox input {
  position: absolute;
  opacity: 0;

  width: 0;
  height: 0;

  margin: 0;
  padding: 0;

  pointer-events: none;
}

.custom-checkbox {
  width: 26px;
  height: 26px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #ffffff;

  border: 1.5px solid #b8bcc4;
  border-radius: 8px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.confirmation-checkbox.checked
.custom-checkbox {
  background: #2887f7;
  border-color: #2887f7;

  box-shadow:
    0 5px 12px
    rgba(40, 135, 247, 0.25);
}

.checkbox-text {
  padding-top: 2px;

  color: #303238;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

/* Error */

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 18px;

  padding:
    14px
    16px;

  color: #c83a3a;
  background: rgba(220, 53, 69, 0.08);

  border: 1px solid rgba(220, 53, 69, 0.16);
  border-radius: 14px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.45;
}

.error-icon {
  width: 21px;
  height: 21px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #dc3545;
  border-radius: 50%;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

/* Footer */

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  flex-shrink: 0;

  padding:
    18px
    40px
    22px;

  background: #ffffff;
}

.drawer-footer button {
  min-height: 54px;
  flex: 0 0 auto;

  padding: 0 24px;

  border: none;
  border-radius: 17px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.secondary-button {
  width: 190px;

  color: #303238;
  background: #f0f1f3;
}

.secondary-button:hover:not(:disabled) {
  background: #e7e9ed;

  transform: translateY(-1px);
}

.accept-button {
  width: 270px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  color: #ffffff;

  background:
    linear-gradient(
      110deg,
      #2887f7,
      #526de8,
      #34a9f4,
      #2887f7
    );

  background-size: 220% 100%;

  box-shadow:
    0 9px 20px
    rgba(40, 135, 247, 0.27);

  animation:
    button-gradient
    3s
    linear
    infinite;
}

.accept-button:hover:not(:disabled) {
  transform: translateY(-1px);

  box-shadow:
    0 12px 25px
    rgba(40, 135, 247, 0.35);
}

.drawer-footer button:disabled {
  cursor: not-allowed;

  opacity: 0.45;

  box-shadow: none;
  transform: none;
}

.button-loader {
  width: 17px;
  height: 17px;

  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}

/* Transitions */

.error-enter-active,
.error-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.error-enter-from,
.error-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Dark theme */

:global(html[data-theme="dark"])
.digital-sign-drawer {
  background: #1c1c1e;
  border-color: rgba(255, 255, 255, 0.08);
}

:global(html[data-theme="dark"])
.drawer-header,
:global(html[data-theme="dark"])
.drawer-footer {
  background: #1c1c1e;
}

:global(html[data-theme="dark"])
.header-text h2,
:global(html[data-theme="dark"])
.licence-section h3,
:global(html[data-theme="dark"])
.confirmation-title,
:global(html[data-theme="dark"])
.confirmation-subtitle,
:global(html[data-theme="dark"])
.checkbox-text {
  color: #f1f1f3;
}

:global(html[data-theme="dark"])
.header-text p,
:global(html[data-theme="dark"])
.introductory-text,
:global(html[data-theme="dark"])
.section-text,
:global(html[data-theme="dark"])
.licence-section li,
:global(html[data-theme="dark"])
.confirmation-card li,
:global(html[data-theme="dark"])
.confirmation-description {
  color: #a7a9af;
}

:global(html[data-theme="dark"])
.introductory-text {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.11),
      rgba(103, 88, 232, 0.07)
    );

  border-color: rgba(40, 135, 247, 0.18);
}

:global(html[data-theme="dark"])
.confirmation-card {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.12),
      rgba(103, 88, 232, 0.08)
    );

  border-color: rgba(40, 135, 247, 0.2);
}

:global(html[data-theme="dark"])
.confirmation-checkbox {
  background: #242427;
  border-color: #343439;
}

:global(html[data-theme="dark"])
.confirmation-checkbox.checked {
  background: rgba(40, 135, 247, 0.12);
  border-color: rgba(40, 135, 247, 0.45);
}

:global(html[data-theme="dark"])
.custom-checkbox {
  background: #29292d;
  border-color: #555860;
}

:global(html[data-theme="dark"])
.confirmation-checkbox.checked
.custom-checkbox {
  background: #2887f7;
  border-color: #2887f7;
}

:global(html[data-theme="dark"])
.close-button,
:global(html[data-theme="dark"])
.secondary-button {
  color: #ededee;
  background: #2b2b30;
}

:global(html[data-theme="dark"])
.drawer-divider {
  background: rgba(255, 255, 255, 0.08);
}

/* Tablet */

@media (max-width: 900px) {
  .digital-sign-overlay {
    padding: 20px;
  }

  .digital-sign-drawer {
    max-width: none;
    max-height: none;
  }

  .drawer-header {
    padding-left: 28px;
    padding-right: 28px;
  }

  .drawer-content {
    padding:
      30px
      32px;
  }

  .drawer-footer {
    padding-left: 28px;
    padding-right: 28px;
  }
}

/* Mobile */

@media (max-width: 640px) {
  .digital-sign-overlay {
    align-items: flex-end;

    padding: 0;
  }

  .digital-sign-drawer {
    width: 100%;
    height: 96dvh;

    max-width: none;
    max-height: 96dvh;

    border-left: 0;
    border-right: 0;
    border-bottom: 0;

    border-radius:
      26px
      26px
      0
      0;
  }

  .drawer-indicator {
    margin-top: 9px;
  }

  .drawer-header {
    gap: 12px;

    padding:
      13px
      16px
      18px;
  }

  .header-icon {
    width: 46px;
    height: 46px;

    border-radius: 14px;

    font-size: 19px;
  }

  .header-text h2 {
    font-size: 17px;
  }

  .header-text p {
    font-size: 12px;
  }

  .close-button {
    width: 34px;
    height: 34px;

    font-size: 21px;
  }

  .drawer-content {
    padding:
      22px
      16px;
  }

  .introductory-text {
    padding: 17px;
  }

  .licence-section {
    margin-top: 25px;
  }

  .licence-section h3 {
    font-size: 17px;
  }

  .confirmation-card {
    margin-top: 28px;
    padding: 17px;
  }

  .confirmation-checkbox {
    padding: 15px;
  }

  .drawer-footer {
    padding:
      14px
      14px
      calc(
        14px +
        env(safe-area-inset-bottom)
      );
  }

  .drawer-footer button {
    min-height: 52px;
    flex: 1;

    width: auto;

    padding: 0 12px;

    font-size: 14px;
  }
}

@media (max-width: 390px) {
  .header-text p {
    display: none;
  }

  .drawer-footer {
    gap: 9px;
  }

  .drawer-footer button {
    font-size: 13px;
  }
}

/* Animations */

@keyframes overlay-appear {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes drawer-appear {
  from {
    opacity: 0;

    transform:
      translateY(35px)
      scale(0.985);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

@keyframes button-gradient {
  from {
    background-position: 0 50%;
  }

  to {
    background-position: 220% 50%;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .digital-sign-overlay,
  .digital-sign-drawer,
  .accept-button,
  .button-loader {
    animation: none;
  }

  .confirmation-checkbox,
  .drawer-footer button,
  .close-button {
    transition: none;
  }
}

/* DARK THEME */

html[data-theme="dark"] .digital-sign-overlay {
  background: rgba(0, 0, 0, 0.72);
}

html[data-theme="dark"] .digital-sign-drawer {
  background-color: #1f1f22;
  border-color: rgba(255, 255, 255, 0.08);

  box-shadow:
    0 40px 110px
    rgba(0, 0, 0, 0.62);
}

html[data-theme="dark"] .drawer-indicator {
  background-color: #4a4a4f;
}

html[data-theme="dark"] .drawer-header {
  background-color: #1f1f22;
}

html[data-theme="dark"] .drawer-footer {
  background-color: #1f1f22;
}

html[data-theme="dark"] .drawer-divider {
  background-color: rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"] .header-text h2 {
  color: #f5f5f7;
}

html[data-theme="dark"] .header-text p {
  color: #92949b;
}

html[data-theme="dark"] .close-button {
  color: #d7d7db;
  background-color: #2c2c30;
}

html[data-theme="dark"] .close-button:hover:not(:disabled) {
  color: #ffffff;
  background-color: #37373c;
}

html[data-theme="dark"] .drawer-content {
  background-color: #1f1f22;
}

html[data-theme="dark"] .drawer-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid transparent;
  background-clip: padding-box;
}

html[data-theme="dark"] .introductory-text {
  color: #a8aab1;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.13),
      rgba(103, 88, 232, 0.08)
    );

  border-color: rgba(40, 135, 247, 0.22);
}

html[data-theme="dark"] .licence-section h3 {
  color: #f2f2f4;
}

html[data-theme="dark"] .section-text {
  color: #a8aab1;
}

html[data-theme="dark"] .licence-section li {
  color: #a8aab1;
}

html[data-theme="dark"] .confirmation-card {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.15),
      rgba(103, 88, 232, 0.10)
    );

  border-color: rgba(40, 135, 247, 0.24);
}

html[data-theme="dark"] .confirmation-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .confirmation-subtitle {
  color: #d5d5d9;
}

html[data-theme="dark"] .confirmation-card li {
  color: #a8aab1;
}

html[data-theme="dark"] .confirmation-description {
  color: #8f9198;
}

html[data-theme="dark"] .confirmation-checkbox {
  background-color: #27272b;
  border-color: #39393f;
}

html[data-theme="dark"] .confirmation-checkbox:hover {
  border-color: rgba(40, 135, 247, 0.42);

  box-shadow:
    0 8px 24px
    rgba(0, 0, 0, 0.22);
}

html[data-theme="dark"] .confirmation-checkbox.checked {
  background-color: rgba(40, 135, 247, 0.13);
  border-color: rgba(40, 135, 247, 0.52);
}

html[data-theme="dark"] .custom-checkbox {
  color: #ffffff;
  background-color: #303035;
  border-color: #585860;
}

html[data-theme="dark"] .confirmation-checkbox.checked .custom-checkbox {
  background-color: #2887f7;
  border-color: #2887f7;

  box-shadow:
    0 5px 14px
    rgba(40, 135, 247, 0.32);
}

html[data-theme="dark"] .checkbox-text {
  color: #ededf0;
}

html[data-theme="dark"] .error-message {
  color: #ff8d94;
  background-color: rgba(220, 53, 69, 0.13);
  border-color: rgba(220, 53, 69, 0.24);
}

html[data-theme="dark"] .secondary-button {
  color: #ededf0;
  background-color: #2d2d32;
}

html[data-theme="dark"] .secondary-button:hover:not(:disabled) {
  background-color: #38383e;
}

html[data-theme="dark"] .accept-button {
  color: #ffffff;

  box-shadow:
    0 10px 24px
    rgba(40, 135, 247, 0.34);
}

html[data-theme="dark"] .accept-button:hover:not(:disabled) {
  box-shadow:
    0 14px 30px
    rgba(40, 135, 247, 0.42);
}

.confirmation-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 13px;

  margin-top: 22px;

  padding: 17px 18px;

  background: #f7f8fa;
  border: 1px solid #e3e6eb;
  border-radius: 17px;

  cursor: pointer;
  user-select: none;

  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.confirmation-checkbox:focus-visible {
  outline: 2px solid rgba(40, 135, 247, 0.45);
  outline-offset: 3px;
}

.confirmation-checkbox.checked {
  background: rgba(40, 135, 247, 0.07);
  border-color: rgba(40, 135, 247, 0.4);
}

.custom-checkbox {
  width: 26px;
  height: 26px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #ffffff;

  border: 1.5px solid #b8bcc4;
  border-radius: 8px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.confirmation-checkbox.checked .custom-checkbox {
  background: #2887f7;
  border-color: #2887f7;

  box-shadow:
    0 5px 12px
    rgba(40, 135, 247, 0.25);
}
</style>